import { userFacingError } from '../utils/userFacingError';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EditorDraft, EditorDraftService, DraftConflictError } from '../services/editorDraftService';

export default function useEditorDraft<T>({ ownerId, editorKey, value, enabled, hasContent, onRestore }: {
  ownerId?: string; editorKey: string; value: T; enabled: boolean; hasContent: boolean; onRestore: (value: T) => void;
}) {
  const serialized = JSON.stringify(value);
  const latest = useRef({ value, serialized, hasContent, onRestore });
  latest.current = { value, serialized, hasContent, onRestore };
  const baseline = useRef(serialized);
  const version = useRef<string | null>(null);
  const active = useRef(false);
  const stopped = useRef(false);
  const conflict = useRef(false);
  const inFlight = useRef<Promise<void> | null>(null);
  const [ready, setReady] = useState(false);
  const [recovery, setRecovery] = useState<EditorDraft<T> | null>(null);
  const recoveryRef = useRef<EditorDraft<T> | null>(null);
  const [status, setStatus] = useState<'loading' | 'idle' | 'saving' | 'saved' | 'error'>('loading');
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [, refresh] = useState(0);

  const load = useCallback(async () => {
    if (!ownerId) return;
    setReady(false); setStatus('loading'); setError(''); conflict.current = false;
    try {
      const draft = await EditorDraftService.load<T>(ownerId, editorKey);
      if (!active.current) return;
      version.current = draft?.updatedAt ?? null;
      recoveryRef.current = draft; setRecovery(draft);
      setSavedAt(draft?.updatedAt ?? null); setReady(true); setStatus('idle');
    } catch (e) {
      if (active.current) { setError(userFacingError(e, 'Automatische Sicherung nicht erreichbar.')); setStatus('error'); }
    }
  }, [ownerId, editorKey]);

  useEffect(() => {
    active.current = true; stopped.current = false;
    if (enabled) { baseline.current = latest.current.serialized; void load(); }
    return () => { active.current = false; };
  }, [enabled, load]);

  const saveNow = useCallback(async () => {
    if (!ownerId || !ready || recoveryRef.current || stopped.current || conflict.current) return;
    if (inFlight.current) return inFlight.current;
    const work = async () => {
      try {
        while (!stopped.current && latest.current.serialized !== baseline.current && (latest.current.hasContent || version.current)) {
          const snapshot = latest.current;
          if (active.current) { setStatus('saving'); setError(''); }
          const saved = await EditorDraftService.save(ownerId, editorKey, snapshot.value, version.current);
          version.current = saved.updatedAt; baseline.current = snapshot.serialized;
          if (active.current) { setSavedAt(saved.updatedAt); setStatus('saved'); refresh(n => n + 1); }
        }
      } catch (e) {
        conflict.current = e instanceof DraftConflictError;
        if (active.current) { setError(userFacingError(e, 'Automatische Sicherung fehlgeschlagen.')); setStatus('error'); }
        throw e;
      }
    };
    const pending = work(); inFlight.current = pending;
    try { await pending; } finally { inFlight.current = null; }
  }, [ownerId, editorKey, ready]);

  const dirty = enabled && !recovery && serialized !== baseline.current && (hasContent || !!version.current);
  useEffect(() => {
    if (!enabled || !ready || !dirty || conflict.current) return;
    const timer = window.setTimeout(() => { void saveNow().catch(() => undefined); }, 2000);
    return () => window.clearTimeout(timer);
  }, [enabled, ready, dirty, serialized, saveNow]);

  useEffect(() => {
    const retry = () => { if (ready) void saveNow().catch(() => undefined); else void load(); };
    const onHidden = () => { if (document.visibilityState === 'hidden') retry(); };
    window.addEventListener('online', retry); document.addEventListener('visibilitychange', onHidden);
    return () => { window.removeEventListener('online', retry); document.removeEventListener('visibilitychange', onHidden); };
  }, [ready, load, saveNow]);

  const restore = () => {
    if (!recoveryRef.current) return;
    const saved = recoveryRef.current;
    baseline.current = JSON.stringify(saved.value);
    latest.current.onRestore(saved.value);
    recoveryRef.current = null; setRecovery(null); setStatus('saved');
  };
  const discardRecovery = async () => {
    if (!ownerId) return;
    try {
      await EditorDraftService.remove(ownerId, editorKey, version.current);
      version.current = null; recoveryRef.current = null; baseline.current = latest.current.serialized;
      setRecovery(null); setSavedAt(null); setError(''); setStatus('idle'); refresh(n => n + 1);
    } catch (e) { setError(userFacingError(e, 'Bearbeitungsstand konnte nicht verworfen werden.')); setStatus('error'); }
  };
  const complete = async () => {
    stopped.current = true;
    if (inFlight.current) await inFlight.current.catch(() => undefined);
    if (ownerId) await EditorDraftService.remove(ownerId, editorKey, version.current);
    baseline.current = latest.current.serialized; refresh(n => n + 1);
  };
  return { ready, recovery, restore, discardRecovery, status, error, savedAt, dirty, saveNow, complete, retry: () => ready ? saveNow() : load() };
}
